import React, { useCallback, useEffect, useState } from "react";
import { AuthorizedLayout } from "../Layouts/AuthorizedLayout";
import BoxHeader from "../../Components/Box/BoxHeader";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Box,
  FormLabel,
  Grid,
} from "@mui/material";
import { useDispatch } from "react-redux";
import LocationIcon from "../../Assets/images/menu-icons/location_away_selected.png";
import { AntTab, AntTabs } from "../../Components/Tab/AntTab";
import { PermissionCheck } from "../../App/PermissionCheck";
import { GetApiMethod, PostApiMethod, PutApiMethod, DeleteApiMethod } from "../../Utils/ApiService";
import { showLoader } from "../../Store/loaderSlice";
import { showToaster } from "../../Store/toastSlice";
import MenuGroup from "../../Components/DropDown/MenuGroup";
import { CustomModal, ModalStyle } from "../../Components/ModalStyle";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { CustomTablePagination } from "../../Components/Pagination/CustomTablePagination";
import TableSearch from "../../Components/Search/TableSearch"

interface LocationDto {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
}

interface LocationListDto {
  records: LocationDto[];
  totalCount: number;
}

const LocationManagement = () => {
  const [locationList, setLocationList] = useState<LocationListDto>({
    records: [],
    totalCount: 0,
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [formModal, setFormModal] = useState(false);
  const [editLocation, setEditLocation] = useState<LocationDto | null>(null);
  const dispatch = useDispatch();

  const getLocations = useCallback(async () => {
    try {
      dispatch(showLoader({ showLoader: true }));
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        search: searchTerm.trim(),
        sort: "createdAt",
        sortDirection: "DESC"
      });
      const res = await GetApiMethod(`location?${params.toString()}`);
      console.log('Fetched locations:', res);
      const responseData = {
        records: res.data.data.rows || [],
        totalCount: res.data.data.totalCount || 0
      };
      console.log('Fetched responseData:', responseData);

      setLocationList(responseData);
      dispatch(showLoader({ showLoader: false }));
    } catch (err) {
      dispatch(showLoader({ showLoader: false }));
    }
  }, [dispatch, page, rowsPerPage, searchTerm]);


const validationSchema = useCallback(() => 
  Yup.object({
    name: Yup.string()
      .trim()
      .required("Location name is required")
      .test("unique-name", "Location with this name already exists", (value) => {
        if (!value) return true;
        return !(locationList.records || []).some(
          loc => loc.name.toLowerCase() === value.toLowerCase() && loc.id !== editLocation?.id
        );
      }),
    latitude: Yup.number()
      .required("Latitude is required")
      .min(-90, "Latitude must be >= -90")
      .max(90, "Latitude must be <= 90"),
    longitude: Yup.number()
      .required("Longitude is required")
      .min(-180, "Longitude must be >= -180")
      .max(180, "Longitude must be <= 180"),
  })
, [locationList.records, editLocation?.id]);

  const handleSaveLocation = async (values: { name: string; latitude: string; longitude: string }) => {
  try {
    const data = {
      name: values.name.trim(),
      latitude: Number(values.latitude),
      longitude: Number(values.longitude)
    };

    if (isNaN(data.latitude) || isNaN(data.longitude)) {
      dispatch(showToaster({
        dialogBgColor: "bg-danger",
        dialogMsg: "Please enter valid numbers for latitude and longitude",
        showDialog: true,
        timer: "3000",
      }));
      return;
    }

    const isDuplicate = (locationList.records || []).some(location =>
      location.name.toLowerCase() === data.name.toLowerCase() && location.id !== editLocation?.id
    );

    if (isDuplicate) {
      dispatch(showToaster({
        dialogBgColor: "bg-warning",
        dialogMsg: "Location with this name already exists",
        showDialog: true,
        timer: "3000",
      }));
      return;
    }

    dispatch(showLoader({ showLoader: true }));

    if (editLocation?.id) {
      await PutApiMethod(`location/${editLocation.id}`, data);
    } else {
      await PostApiMethod("location", data);
    }

    setFormModal(false);
    setEditLocation(null);

    await getLocations(); 
    dispatch(showLoader({ showLoader: false }));
    dispatch(showToaster({
      dialogBgColor: "bg-success",
      dialogMsg: `Location ${editLocation?.id ? "updated" : "created"} successfully`,
      showDialog: true,
      timer: "3000",
    }));

  } catch (err: any) {
    console.error('Error saving location:', err);
    dispatch(showToaster({
      dialogBgColor: "bg-danger",
      dialogMsg: err?.response?.data?.message || "Failed to save location",
      showDialog: true,
      timer: "3000",
    }));
    dispatch(showLoader({ showLoader: false }));
  }
};


  const resetForm = useCallback(() => {
    setFormModal(false);
    setEditLocation(null);
  }, []);

  const handleDeleteLocation = async (id: string) => {
    try {
      dispatch(showLoader({ showLoader: true }));
      await DeleteApiMethod(`location/${id}`);
      await getLocations();
      dispatch(showLoader({ showLoader: false }));
    } catch (err) {
      console.error('Error deleting location:', err);
      dispatch(showLoader({ showLoader: false }));
    }
  };

  useEffect(() => {
    getLocations();
  }, [getLocations]);

  return (
    <AuthorizedLayout>
      <BoxHeader
        dashboard_name="Location Management"
        button_name="Add Location"
        image={LocationIcon}
        onAdd={() => { setFormModal(true); setEditLocation(null); }}
        button_show={PermissionCheck("manage_locations")}
      >
        <AntTabs value={0}>
          <AntTab label="All Locations" className="tab-menu" />
        </AntTabs>
      </BoxHeader>

      <TableSearch
        value={searchTerm}
        onChange={(value) => { setSearchTerm(value); setPage(0); }}
        placeholder="Search locations"
      />

      <TableContainer className="mt-2">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Latitude</TableCell>
              <TableCell>Longitude</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(locationList.records || []).map((location, key) => (
              <TableRow key={key}>
                <TableCell>{location.name}</TableCell>
                <TableCell>{location.latitude}</TableCell>
                <TableCell>{location.longitude}</TableCell>
                <TableCell align="right">
                  <MenuGroup
                    id={location.id}
                    view={false}
                    edit={true}
                    delete={true}
                    clickEdit={() => { setEditLocation(location); setFormModal(true); }}
                    clickDelete={() => handleDeleteLocation(location.id)}
                  />
                </TableCell>
              </TableRow>
            ))}
            {locationList.records.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">No Data</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <CustomTablePagination
        count={locationList.totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); }}
      />

      <CustomModal
        open={formModal}
        onClose={() => { resetForm(); dispatch(showLoader({ showLoader: false })); }}
        header={`${editLocation?.id ? "Edit" : "Create"} Location`}
        style={ModalStyle({ width: 600, height: "auto" })}
      >
        <Formik
          key={editLocation?.id || 'new'}
          initialValues={{
            name: editLocation?.name || "",
            latitude: editLocation?.latitude?.toString() || "",
            longitude: editLocation?.longitude?.toString() || "",
          }}
          validationSchema={validationSchema()}
          onSubmit={async (values) => { await handleSaveLocation(values); }}
          enableReinitialize={true}
        >
          {({ handleSubmit, handleChange, values, errors, touched }) => (
            <Form onSubmit={handleSubmit}>
              <Box className="modal-body">
                <Grid item md={12} m={2}>
                  <Grid container spacing={1} className="p-3">
                    <FormLabel className="fw-bold">Location Name:<sup className="text-danger">*</sup></FormLabel>
                    <TextField
                      className="w-100"
                      placeholder="Enter location name..."
                      variant="standard"
                      value={values.name}
                      name="name"
                      onChange={handleChange}
                    />
                    {touched.name && errors.name && (<div className="text-danger">{errors.name}</div>)}
                  </Grid>

                  <Grid container spacing={1} className="p-3">
                    <FormLabel className="fw-bold">Latitude:<sup className="text-danger">*</sup></FormLabel>
                    <TextField
                      className="w-100"
                      placeholder="Enter latitude..."
                      variant="standard"
                      type="number"
                      value={values.latitude}
                      name="latitude"
                      onChange={handleChange}
                    />
                    {touched.latitude && errors.latitude && (<div className="text-danger">{errors.latitude}</div>)}
                  </Grid>

                  <Grid container spacing={1} className="p-3">
                    <FormLabel className="fw-bold">Longitude:<sup className="text-danger">*</sup></FormLabel>
                    <TextField
                      className="w-100"
                      placeholder="Enter longitude..."
                      variant="standard"
                      type="number"
                      value={values.longitude}
                      name="longitude"
                      onChange={handleChange}
                    />
                    {touched.longitude && errors.longitude && (<div className="text-danger">{errors.longitude}</div>)}
                  </Grid>
                </Grid>
                <Box className="d-flex justify-content-end mt-3 p-3">
                  <button className="btn btn-danger" type="submit">Save</button>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </CustomModal>
    </AuthorizedLayout>
  );
};

export default LocationManagement;
